import { useMemo } from 'react';
import { Flame, Target, TrendingUp, Brain, GraduationCap } from 'lucide-react';
import type { FlashcardStats } from '../../services/flashcardService';
import type { Tutee } from '../../types/tuition';
import FlashcardRatingsPanel from './FlashcardRatingsPanel';
import FlashcardStudyHeatmap from './FlashcardStudyHeatmap';
import FlashcardForecastChart from './FlashcardForecastChart';

interface FlashcardsProgressProps {
  tutee: Tutee;
  stats: FlashcardStats;
  onStartStudy: () => void;
  /** Home: study-focused. Analytics: full charts on Progress tab. */
  mode?: 'home' | 'analytics';
  scopeLabel?: string;
}

const FlashcardsProgress = ({ tutee, stats, onStartStudy, mode = 'home', scopeLabel }: FlashcardsProgressProps) => {
  const primary = tutee.colorScheme.primary;
  const showAnalytics = mode === 'analytics';

  const ringPct =
    stats.dueNow + stats.reviewsToday > 0
      ? Math.min(100, (stats.reviewsToday / Math.max(stats.dueNow + stats.reviewsToday, 1)) * 100)
      : 100;

  const ratingCountsByPeriod =
    stats.ratingCountsByPeriod ?? {
      today: stats.ratingCounts,
      week: stats.ratingCounts,
      month: stats.ratingCounts,
    };

  return (
    <div className="space-y-6 animate-fade-in">
      {scopeLabel && (
        <p className="text-xs font-bold text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-xl px-3 py-2">
          Showing stats for: {scopeLabel}
        </p>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Due now', value: stats.dueNow, icon: Target, color: 'text-red-600', hint: '' },
          {
            label: 'Reviews today',
            value: stats.reviewsToday,
            icon: TrendingUp,
            color: 'text-indigo-600',
            hint: '',
          },
          { label: 'Streak', value: stats.streak, icon: Flame, color: 'text-orange-600', hint: 'days in a row' },
          {
            label: 'Well learned',
            value: stats.matureCards,
            icon: Brain,
            color: 'text-emerald-600',
            hint: '21+ day interval',
          },
        ].map(({ label, value, icon: Icon, color, hint }) => (
          <div
            key={label}
            className="bg-white/80 backdrop-blur rounded-2xl p-4 shadow-lg border border-white/60"
            title={label === 'Well learned' ? 'Cards you rated Good/Easy enough times to reach a 21-day or longer review interval' : undefined}
          >
            <Icon className={`w-5 h-5 mb-2 ${color}`} />
            <p className="text-2xl font-black text-gray-900">{value}</p>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">{label}</p>
            {hint && <p className="text-[10px] text-gray-400 mt-0.5 font-medium">{hint}</p>}
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row items-center gap-6 bg-white/80 backdrop-blur rounded-2xl p-6 shadow-lg border border-white/60">
        <div className="relative w-28 h-28 flex-shrink-0">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
            <path
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none"
              stroke="#e5e7eb"
              strokeWidth="3"
            />
            <path
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeDasharray={`${ringPct}, 100`}
              className={`text-${primary}-500`}
              style={{ stroke: 'var(--ring-color, #6366f1)' }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-xl font-black">{Math.round(ringPct)}%</span>
            <span className="text-[10px] font-bold text-gray-500">today</span>
          </div>
        </div>
        <div className="flex-1 text-center sm:text-left">
          <p className="text-lg font-bold text-gray-800">
            {stats.totalActive} active cards · avg ease {stats.averageEase.toFixed(2)}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {stats.newToday} new introduced today · {stats.dueNow} waiting for you
          </p>
          {stats.matureCards > 0 && (
            <p className="text-xs text-emerald-700 mt-2 flex items-center justify-center sm:justify-start gap-1 font-semibold">
              <GraduationCap className="w-3.5 h-3.5" />
              {stats.matureCards} well learned (long-interval review)
            </p>
          )}
          <button
            type="button"
            onClick={onStartStudy}
            className={`mt-4 px-6 py-3 rounded-xl font-bold text-white bg-gradient-to-r ${tutee.colorScheme.gradient} shadow-lg hover:shadow-xl active:scale-95 transition-all`}
          >
            {stats.dueNow > 0 ? `Study ${stats.dueNow} cards` : 'Study anyway'}
          </button>
        </div>
      </div>

      <FlashcardStudyHeatmap heatmap={stats.heatmap} />

      <FlashcardRatingsPanel ratingCountsByPeriod={ratingCountsByPeriod} />

      {showAnalytics && <FlashcardForecastChart forecast={stats.forecast} />}
    </div>
  );
};

export default FlashcardsProgress;
